/**
 * A mock platform, so this app runs and can be judged before `/v1/me/*` is
 * reachable from a browser and before any customer exists.
 *
 * It answers the same shapes the real client does, which is the point: the
 * app cannot tell the difference, so nothing downstream is written twice.
 * Three moments are provided because day one and the closed season are the
 * states a demo actually opens on, and both are easy to get wrong.
 *
 * Everything here is fiction about a fictional customer, Lumen Studio. No
 * value in this file is real, and none of it ships to a tenant.
 */

import type {
  MeBadge,
  MeCampaign,
  MeGrants,
  MeProgress,
  SeasonTrack,
  WidgetConfig,
  WidgetSurfaces,
} from "./contract";
import type { PlayClient } from "./client";

export type Moment = "fresh" | "mid" | "summit";

interface Seed {
  title: string;
  events: string[];
  target: number;
  coins: number;
  /** Where this quest stands at the mid season moment. */
  mid: { enrollment: MeCampaign["enrollment"]; current: number; completedAt: string | null };
}

/**
 * The player facing words live here on purpose. The platform publishes the
 * campaign's own facts, its events and its goal, and never a dashboard string
 * (contract 0.3.0, Station card). Writing the sentence is this side's job, and
 * a vocabulary pack is what will do it. Until a pack exists these stand in.
 */
const SEEDS: Seed[] = [
  { title: "First render", events: ["render.created"], target: 1, coins: 40,
    mid: { enrollment: "completed", current: 1, completedAt: "2026-08-19T09:12:00Z" } },
  { title: "Pick a style", events: ["style.applied"], target: 1, coins: 25,
    mid: { enrollment: "completed", current: 1, completedAt: "2026-08-19T14:03:00Z" } },
  { title: "Three in a row", events: ["render.created"], target: 3, coins: 40,
    mid: { enrollment: "completed", current: 3, completedAt: "2026-08-21T10:40:00Z" } },
  { title: "Share your work", events: ["render.published"], target: 1, coins: 60,
    mid: { enrollment: "completed", current: 1, completedAt: "2026-08-22T16:20:00Z" } },
  { title: "Upscale it", events: ["render.upscaled"], target: 1, coins: 30,
    mid: { enrollment: "completed", current: 1, completedAt: "2026-08-23T11:05:00Z" } },
  { title: "Generate 3 images", events: ["render.created"], target: 3, coins: 40,
    mid: { enrollment: "enrolled", current: 2, completedAt: null } },
  { title: "Try style transfer", events: ["style.transferred"], target: 1, coins: 35,
    mid: { enrollment: "not_enrolled", current: 0, completedAt: null } },
  { title: "Invite a teammate", events: ["member.invited"], target: 1, coins: 80,
    mid: { enrollment: "not_enrolled", current: 0, completedAt: null } },
  { title: "Batch of ten", events: ["render.created"], target: 10, coins: 60,
    mid: { enrollment: "not_enrolled", current: 0, completedAt: null } },
];

/**
 * The words a card shows, keyed by the campaign id the mock mints. In the
 * shipped app this map is the vocabulary pack's job; keeping it beside the
 * seeds rather than inside `MeCampaign` is deliberate, because the platform
 * never sends these and the type should keep saying so.
 */
export const MOCK_COPY: Record<string, { title: string; desc: string }> = {};

function campaignsFor(moment: Moment): MeCampaign[] {
  return SEEDS.map((seed, i) => {
    const id = `cmp_${String(i + 1).padStart(2, "0")}`;
    MOCK_COPY[id] = { title: seed.title, desc: describe(seed) };

    const stand =
      moment === "fresh"
        ? { enrollment: "not_enrolled" as const, current: 0, completedAt: null }
        : moment === "summit"
          ? { enrollment: "completed" as const, current: seed.target, completedAt: "2026-09-01T12:00:00Z" }
          : seed.mid;

    // The first quest of a fresh season is the one the player is standing on,
    // so it is enrolled with nothing done rather than untouched.
    const enrollment = moment === "fresh" && i === 0 ? ("enrolled" as const) : stand.enrollment;

    return {
      id,
      status: "live",
      enrollment,
      goal: { kind: seed.events[0] ?? "event", current: stand.current, target: seed.target },
      events: seed.events,
      reward:
        stand.completedAt !== null
          ? {
              source: "grant",
              status: "fulfilled",
              reward: { kind: "credits", value: seed.coins, currency: "coins" },
            }
          : {
              source: "campaign",
              reward: { kind: "credits", value: seed.coins, currency: "coins" },
            },
      startsAt: null,
      completedAt: stand.completedAt,
    } satisfies MeCampaign;
  });
}

/** A sentence from the campaign's own published facts, which is all we get. */
function describe(seed: Seed): string {
  const noun = seed.events[0]?.split(".")[0] ?? "action";
  if (seed.target > 1) return `Reach ${seed.target} in Lumen Studio. Progress updates as you work.`;
  return `Complete this ${noun} in Lumen Studio. Progress updates as you work.`;
}

function seasonFor(moment: Moment): SeasonTrack {
  const earned = (tier: number) =>
    moment === "summit" ? true : moment === "fresh" ? false : tier <= 2;
  return {
    name: "Season 1",
    tier: moment === "summit" ? 5 : moment === "fresh" ? 0 : 2,
    tierCount: 5,
    rewards: [
      { tier: 2, reward: { kind: "credits", value: 120, currency: "coins" }, earned: earned(2), requirement: null },
      { tier: 3, reward: { kind: "custom", value: "Prism cluster" }, earned: earned(3), requirement: "Finish 2 more Relay quests" },
      { tier: 4, reward: { kind: "credits", value: 300, currency: "coins" }, earned: earned(4), requirement: "Reach tier 4" },
    ],
    summit: { reward: { kind: "custom", value: "Grand kit" }, earned: moment === "summit" },
  };
}

function balanceFor(moment: Moment): number {
  return moment === "fresh" ? 0 : moment === "summit" ? 3180 : 2260;
}

export interface MockOptions {
  moment: Moment;
  template: WidgetConfig["template"];
  surfaces?: Partial<WidgetSurfaces>;
  /** Milliseconds of pretend latency, so loading states are visible in dev. */
  latencyMs?: number;
}

const DEFAULT_SURFACES: WidgetSurfaces = {
  streak: true,
  checklist: true,
  milestones: true,
  leaderboard: false,
  profile: true,
  // Off is the platform default, and coins follow the store. Turn it on in the
  // dev bar to see every coin surface appear at once.
  store: true,
};

export function createMockClient(options: MockOptions): PlayClient {
  const wait = <T>(value: T): Promise<T> =>
    new Promise((resolve) => setTimeout(() => resolve(value), options.latencyMs ?? 0));

  return {
    widgetConfig: () =>
      wait<WidgetConfig>({
        environment: "sandbox",
        template: options.template,
        companionTier: "off",
        surfaces: { ...DEFAULT_SURFACES, ...options.surfaces },
        brandAccent: null,
        packPin: "default@0.2.0",
      }),

    progress: () =>
      wait<MeProgress>({
        environment: "sandbox",
        campaigns: campaignsFor(options.moment),
        campaignCount: SEEDS.length,
        balances: [{ currency: "coins", amount: balanceFor(options.moment) }],
        streak: { current: options.moment === "fresh" ? 1 : 6, best: 9 },
        season: seasonFor(options.moment),
      }),

    grants: () =>
      wait<MeGrants>({
        environment: "sandbox",
        grants: [],
        grantCount: 0,
      }),

    badge: () => wait<MeBadge>({ unacknowledged: options.moment === "mid" }),
  };
}
